import { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  LifeBuoy,
  Plus,
  Inbox,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Users,
  Send,
  ArrowRight,
} from "lucide-react";

const CATEGORIES = [
  { v: "registration", label: "Registration issue" },
  { v: "payment", label: "Payment issue" },
  { v: "allocation", label: "Country / committee allocation" },
  { v: "certificate", label: "Certificate issue" },
  { v: "lms_access", label: "LMS access issue" },
  { v: "schedule_room", label: "Schedule / room issue" },
  { v: "parent_consent", label: "Parent consent issue" },
  { v: "technical", label: "Technical issue" },
  { v: "emergency", label: "Emergency support" },
  { v: "general", label: "General query" },
];

const PRIORITIES = [
  { v: "low", label: "Low", color: "bg-slate-100 text-slate-600" },
  { v: "medium", label: "Medium", color: "bg-blue-100 text-blue-700" },
  { v: "high", label: "High", color: "bg-amber-100 text-amber-700" },
  { v: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

const STATUSES = [
  { v: "open", label: "Open", color: "bg-amber-100 text-amber-700" },
  { v: "in_progress", label: "In progress", color: "bg-blue-100 text-blue-700" },
  { v: "waiting_user", label: "Waiting on user", color: "bg-purple-100 text-purple-700" },
  { v: "resolved", label: "Resolved", color: "bg-emerald-100 text-emerald-700" },
  { v: "closed", label: "Closed", color: "bg-slate-100 text-slate-600" },
];

export default function HelpdeskPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [filter, setFilter] = useState({ status: "", category: "", priority: "" });
  const [form, setForm] = useState({
    title: "",
    category: "general",
    priority: "medium",
    description: "",
  });

  const isStaff = ["super_admin", "org_admin", "event_manager", "volunteer"].includes(user?.role);

  const load = () => {
    const params = new URLSearchParams();
    if (filter.status) params.set("status", filter.status);
    if (filter.category) params.set("category", filter.category);
    if (filter.priority) params.set("priority", filter.priority);
    api.get(`/tickets?${params.toString()}`).then((r) => setTickets(r.data || []));
    api.get("/tickets/stats").then((r) => setStats(r.data));
  };

  useEffect(load, [filter]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket raised — we'll get back to you");
      setOpenCreate(false);
      setForm({ title: "", category: "general", priority: "medium", description: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create ticket");
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      await api.post("/tickets/reply", { ticket_id: active.id, message: reply });
      const r = await api.get(`/tickets/${active.id}`);
      setActive(r.data);
      setReply("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reply");
    }
  };

  const updateStatus = async (newStatus) => {
    if (!active) return;
    try {
      await api.patch(`/tickets/${active.id}`, { ticket_id: active.id, status: newStatus });
      const r = await api.get(`/tickets/${active.id}`);
      setActive(r.data);
      load();
      toast.success(`Marked as ${newStatus.replace("_", " ")}`);
    } catch (err) {
      toast.error("Couldn't update");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Helpdesk</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Get the <span className="font-serif italic text-[#0F4C81]">help you need</span>.
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {isStaff ? "Triage and resolve user issues across the conference." : "Raise a ticket — our team usually replies within 1 hour during event days."}
            </p>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="new-ticket-button">
                <Plus className="h-4 w-4 mr-1" /> Raise ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Raise a new ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="ticket-form">
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ticket-title" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Category</Label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                      data-testid="ticket-category"
                    >
                      {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <select
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value })}
                      className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                      data-testid="ticket-priority"
                    >
                      {PRIORITIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Describe the issue</Label>
                  <Textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-description" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="ticket-submit">Submit</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ticket-stats">
            <StatCard icon={Inbox} label="Open" value={stats.open} accent="text-amber-600" />
            <StatCard icon={Clock} label="In progress" value={stats.in_progress} accent="text-blue-600" />
            <StatCard icon={AlertTriangle} label="Urgent open" value={stats.urgent_open} accent="text-red-600" />
            <StatCard icon={CheckCircle2} label="Resolved" value={stats.resolved} accent="text-emerald-600" />
            <StatCard icon={Users} label="Total" value={stats.total} accent="text-[#0A192F]" />
          </div>
        )}

        {/* Filters */}
        {isStaff && (
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="ticket-filters">
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
            <select value={filter.priority} onChange={(e) => setFilter({ ...filter, priority: e.target.value })} className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white">
              <option value="">All priorities</option>
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
            <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white">
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
        )}

        {/* Ticket list */}
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="ticket-list">
          {tickets.length === 0 ? (
            <div className="p-10 text-center">
              <LifeBuoy className="h-6 w-6 text-slate-400 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">No tickets yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-5 py-3 font-semibold">Subject</th>
                  <th className="px-5 py-3 font-semibold">Category</th>
                  <th className="px-5 py-3 font-semibold">Priority</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Raised by</th>
                  <th className="px-5 py-3 font-semibold">Updated</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tickets.map((t) => {
                  const pri = PRIORITIES.find((p) => p.v === t.priority) || PRIORITIES[1];
                  const sta = STATUSES.find((s) => s.v === t.status) || STATUSES[0];
                  const cat = CATEGORIES.find((c) => c.v === t.category) || CATEGORIES[CATEGORIES.length - 1];
                  return (
                    <tr key={t.id} className="hover:bg-slate-50/80 cursor-pointer" onClick={() => setActive(t)} data-testid={`ticket-row-${t.id}`}>
                      <td className="px-5 py-3 font-semibold text-[#0A192F]">{t.title}</td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{cat.label}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${pri.color}`}>
                          {pri.label}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${sta.color}`}>
                          {sta.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs">
                        <p className="font-semibold text-[#0A192F]">{t.raised_by_name}</p>
                        <p className="text-slate-500 capitalize">{t.raised_by_role?.replace("_", " ")}</p>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">
                        {new Date(t.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 text-right text-slate-400">
                        <ArrowRight className="h-4 w-4 ml-auto" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail / thread */}
        {active && (
          <Dialog open onOpenChange={() => setActive(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-testid="ticket-detail-title">{active.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${(STATUSES.find(s => s.v === active.status) || STATUSES[0]).color}`}>
                    {(STATUSES.find(s => s.v === active.status) || STATUSES[0]).label}
                  </span>
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${(PRIORITIES.find(p => p.v === active.priority) || PRIORITIES[1]).color}`}>
                    {(PRIORITIES.find(p => p.v === active.priority) || PRIORITIES[1]).label}
                  </span>
                  <span className="text-xs text-slate-500">
                    Raised by {active.raised_by_name} · {new Date(active.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-slate-700 whitespace-pre-wrap" data-testid="ticket-description-view">
                  {active.description}
                </div>

                {/* Thread */}
                <div className="mt-4 space-y-2" data-testid="ticket-thread">
                  {(active.thread || []).map((m) => (
                    <div key={m.id} className={`p-3 rounded-md border text-sm ${m.from_role === active.raised_by_role ? "bg-blue-50 border-blue-100" : "bg-emerald-50 border-emerald-100"}`}>
                      <p className="text-xs font-semibold text-[#0A192F]">
                        {m.from_name} <span className="font-normal text-slate-500 capitalize">· {m.from_role?.replace("_", " ")}</span>
                      </p>
                      <p className="mt-1 text-slate-700 whitespace-pre-wrap">{m.message}</p>
                      <p className="mt-1 text-[10px] text-slate-400">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>

                {/* Reply */}
                <div className="mt-3">
                  <Textarea
                    rows={3}
                    placeholder="Reply…"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    data-testid="ticket-reply-input"
                  />
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <Button onClick={sendReply} disabled={!reply.trim()} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="ticket-reply-send">
                      <Send className="h-3.5 w-3.5 mr-1.5" /> Send reply
                    </Button>
                    {isStaff && (
                      <div className="flex items-center gap-2">
                        {STATUSES.filter(s => s.v !== active.status).map((s) => (
                          <button key={s.v} onClick={() => updateStatus(s.v)}
                            className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full hover:opacity-80 ${s.color}`}
                            data-testid={`ticket-mark-${s.v}`}>
                            Mark {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className={`h-4 w-4 ${accent}`} />
      <p className={`mt-3 font-serif text-3xl ${accent}`}>{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</p>
    </div>
  );
}
